import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Edit2,
  Trash,
  Search,
  Plus,
  Pencil,
  User,
  Clock,
  Paperclip,
  Download,
  Copy,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Popconfirm,
  Select,
  Form,
  Tooltip,
  Upload,
  Input as AntdInput,
  Image,
} from "antd";
import type { UploadFile, UploadProps } from "antd/es/upload";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import {
  DropdownService,
  SchemaService,
  DynamicModuleService,
  PtsProjectService,
  UploadService,
} from "@/services/APIs";
import { get } from "@/services/axiosUitils";
import { useSearchParams, useLocation } from "react-router-dom";

const TASK_SCHEMA_NAME = "PTS Task";
const SPRINT_SCHEMA_NAME = "PTS Sprint";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const TASK_CATEGORY_SCHEMA_NAME = "PTS Task Category";
const STATUS_SCHEMA_NAME = "PTS Status";
const WORK_TYPE_SCHEMA_NAME = "PTS Work Type";

// Component to fetch and display employee name in tooltip
const EmployeeTooltipContent = ({
  userId,
}: {
  userId: string | null | undefined;
}) => {
  const { data: employeeData, isLoading } = useQuery({
    queryKey: ["employee-by-user-id", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      try {
        // Use encodeURIComponent to properly encode the model name
        const modelName = encodeURIComponent(EMPLOYEE_SCHEMA_NAME);
        const response = await get<any>(
          `/formily/dynamic-model/${modelName}/`,
          {
            params: {
              users__eq: userId,
            },
          }
        );
        return response;
      } catch (error) {
        console.error("Error fetching employee data:", error);
        return null;
      }
    },
    enabled: !!userId,
  });

  // Extract employee name from response
  const employeeName = useMemo(() => {
    if (!employeeData) {
      return "Loading...";
    }

    // Handle different response formats
    const results =
      employeeData?.results ??
      employeeData?.data ??
      (Array.isArray(employeeData) ? employeeData : []);

    if (results.length === 0) {
      return "Unknown Employee";
    }

    const employee = results[0];

    // Try to find first_name and last_name fields
    const firstName =
      employee.first_name ??
      employee.firstname ??
      employee.fname ??
      employee.firstName ??
      "";
    const lastName =
      employee.last_name ??
      employee.lastname ??
      employee.lname ??
      employee.lastName ??
      "";

    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || "Unknown Employee";
  }, [employeeData]);

  if (isLoading) {
    return <span className="text-gray-700">Loading...</span>;
  }

  return <span className="text-gray-700">{employeeName}</span>;
};

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|bmp|svg)$/i;

function isLikelyImageFile(name: string): boolean {
  return IMAGE_EXT_RE.test(name ?? "");
}

/** Single-file attachment for Task form (stores file UUID; matches Formily file field). */
const TaskAttachmentField = ({
  value,
  onChange,
}: {
  value?: string | null;
  onChange?: (v: string | undefined) => void;
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [displayName, setDisplayName] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  /** Full file URL for download (prefer original `file_path`, not thumbnail). */
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const revokeObjectUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  useEffect(() => {
    return () => revokeObjectUrl();
  }, []);

  useEffect(() => {
    let cancelled = false;
    revokeObjectUrl();
    if (!value || typeof value !== "string") {
      setFileList([]);
      setDisplayName("");
      setPreviewUrl(null);
      setDownloadUrl(null);
      return;
    }
    (async () => {
      try {
        const fileData = await UploadService.getFileById({ id: value });
        if (cancelled || !fileData) {
          return;
        }
        const name = fileData.file_original_name ?? "Attachment";
        const fullPath = fileData.file_path as string | undefined;
        const thumbPath = fileData.file_path_thumbnail as string | undefined;
        const previewSource = thumbPath ?? fullPath ?? null;
        const fetchUrl = fullPath ?? thumbPath ?? null;
        setDisplayName(name);
        setPreviewUrl(
          isLikelyImageFile(name) && previewSource ? previewSource : null
        );
        setDownloadUrl(
          typeof fetchUrl === "string" && fetchUrl.length > 0 ? fetchUrl : null
        );
        setFileList([
          {
            uid: value,
            name,
            status: "done",
            url: previewSource ?? undefined,
          },
        ]);
      } catch {
        if (!cancelled) {
          setFileList([]);
          setDisplayName("");
          setPreviewUrl(null);
          setDownloadUrl(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value]);

  const clearAttachment = () => {
    revokeObjectUrl();
    onChange?.(undefined);
    setFileList([]);
    setDisplayName("");
    setPreviewUrl(null);
    setDownloadUrl(null);
  };

  const handleDownloadAttachment = () => {
    if (!downloadUrl) {
      notify.error("Download link is not available for this file");
      return;
    }
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = displayName || "attachment";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const openDocumentPreview = () => {
    if (!downloadUrl) {
      notify.error("Preview is not available for this file");
      return;
    }
    window.open(downloadUrl, "_blank", "noopener,noreferrer");
  };

  const uploadProps: UploadProps = {
    fileList,
    maxCount: 1,
    showUploadList: false,
    beforeUpload: async file => {
      try {
        revokeObjectUrl();
        const response = await UploadService.uploadFiles({
          folderName: "uploads",
          files: [file],
        });
        let items: unknown[] = [];
        if (Array.isArray(response)) {
          items = response;
        } else if (response && typeof response === "object") {
          items = [response];
        } else if (typeof response === "string") {
          items = [response];
        }
        const first = items[0] as Record<string, unknown> | string | undefined;
        const id =
          typeof first === "string"
            ? first
            : first && typeof first === "object" && first.id !== null
              ? String(first.id)
              : "";
        if (!id) {
          return false;
        }
        onChange?.(id);
        setDisplayName(file.name);
        const serverThumb =
          first &&
          typeof first === "object" &&
          (first.file_path_thumbnail as string | undefined);
        const serverFull =
          first &&
          typeof first === "object" &&
          (first.file_path as string | undefined);
        const pathForDownload =
          (typeof serverFull === "string" && serverFull) ||
          (typeof serverThumb === "string" && serverThumb) ||
          null;
        setDownloadUrl(pathForDownload);
        const serverPath = serverThumb ?? serverFull;
        if (
          isLikelyImageFile(file.name) &&
          typeof serverPath === "string" &&
          serverPath.length > 0
        ) {
          setPreviewUrl(serverPath);
        } else if (isLikelyImageFile(file.name)) {
          const objUrl = URL.createObjectURL(file);
          objectUrlRef.current = objUrl;
          setPreviewUrl(objUrl);
        } else {
          setPreviewUrl(null);
        }
        setFileList([
          {
            uid: id,
            name: file.name,
            status: "done",
          },
        ]);
      } catch (e) {
        console.error(e);
        notify.error("Failed to upload file");
      }
      return false;
    },
  };

  const hasFile = Boolean(value && displayName);

  return (
    <div className="task-form-attachment flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
      <div className="shrink-0">
        <Upload {...uploadProps}>
          <Button
            type="button"
            variant="outline"
            className="h-9 w-full rounded-lg border-gray-200 bg-white text-sm sm:w-auto"
          >
            Choose file
          </Button>
        </Upload>
      </div>

      {hasFile && (
        <div className="flex min-h-[4.5rem] w-full min-w-0 flex-1 flex-row items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 sm:min-w-[280px]">
          {previewUrl ? (
            <div className="task-attachment-thumb relative h-14 w-24 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-white sm:h-16 sm:w-28">
              <Image
                src={previewUrl}
                alt={displayName}
                preview={{
                  src: downloadUrl ?? previewUrl,
                  maskClassName: "rounded-md",
                }}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={openDocumentPreview}
              disabled={!downloadUrl}
              className="bg-muted hover:bg-muted/80 flex h-16 w-24 shrink-0 cursor-pointer items-center justify-center rounded-md border border-dashed border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 sm:h-20 sm:w-28"
              aria-label="Open attachment preview"
            >
              <Paperclip className="text-muted-foreground h-7 w-7 sm:h-8 sm:w-8" />
            </button>
          )}
          <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              {!previewUrl && (
                <Paperclip className="text-muted-foreground h-4 w-4 shrink-0" />
              )}
              <span
                className="truncate text-sm font-medium text-[#111827]"
                title={displayName}
              >
                {displayName}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-0.5 rounded-lg bg-gray-100 px-1 py-0.5">
              <button
                type="button"
                onClick={handleDownloadAttachment}
                disabled={!downloadUrl}
                className="rounded p-1.5 text-blue-600 hover:text-blue-500 disabled:pointer-events-none disabled:opacity-40"
                aria-label="Download attachment"
              >
                <Download className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={clearAttachment}
                className="rounded p-1.5 text-red-600 hover:text-red-500"
                aria-label="Remove attachment"
              >
                <Trash className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TaskPage = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Initialize filters from URL parameters
  const urlProject = searchParams.get("project");
  const urlStatus = searchParams.get("status");
  const urlNew = searchParams.get("new") === "1";

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sprintFilter, setSprintFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>(urlStatus ?? "all");
  const [projectFilter, setProjectFilter] = useState<string>(
    urlProject ?? "all"
  );
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [assignedEmployees, setAssignedEmployees] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isFormHighlighted, setIsFormHighlighted] = useState(false);
  const taskFormCardRef = useRef<HTMLDivElement>(null);
  const formHighlightTimeoutRef = useRef<number | null>(null);
  const [selectedProject, setSelectedProject] = useState<string | null>(
    urlProject ?? null
  );

  const scrollToTaskForm = useCallback(() => {
    window.requestAnimationFrame(() => {
      taskFormCardRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setIsFormHighlighted(true);
      if (formHighlightTimeoutRef.current !== null) {
        window.clearTimeout(formHighlightTimeoutRef.current);
      }
      formHighlightTimeoutRef.current = window.setTimeout(() => {
        setIsFormHighlighted(false);
        formHighlightTimeoutRef.current = null;
      }, 2000);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (formHighlightTimeoutRef.current !== null) {
        window.clearTimeout(formHighlightTimeoutRef.current);
      }
    };
  }, []);

  // When navigating with ?new=1&project=id, open create form with project pre-filled and list filtered by project
  useEffect(() => {
    if (urlNew && urlProject) {
      setIsEditMode(true);
      setEditingTaskId(null);
      setSelectedProject(urlProject);
      form.setFieldsValue({ project: urlProject });
    }
  }, [urlNew, urlProject, form]);

  // When navigating from Service Desk Tasks with editTaskId in location state
  useEffect(() => {
    const editTaskId = (location.state as { editTaskId?: string } | null)
      ?.editTaskId;
    if (editTaskId) {
      setEditingTaskId(String(editTaskId));
      setIsEditMode(true);
      scrollToTaskForm();
    }
  }, [location.state, scrollToTaskForm]);

  // Fetch dropdowns
  const { data: projectOptions } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  // Fetch all sprints (for filter dropdown and fallback when no project is selected)
  const { data: allSprintOptions } = useQuery({
    queryKey: ["sprint-dropdown", SPRINT_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(SPRINT_SCHEMA_NAME),
    enabled: !!SPRINT_SCHEMA_NAME,
  });

  // Fetch sprints by project (project-sprint API)
  // const { data: projectSprintOptions } = useQuery({
  //   queryKey: ["project-sprint", selectedProject],
  //   queryFn: async () => {
  //     if (!selectedProject) {
  //       return [];
  //     }
  //     const response = await get<any>("/pts_app/project-sprint/", {
  //       params: {
  //         project_id: selectedProject,
  //       },
  //     });

  //     // Format response to match dropdown format (value/label)
  //     // Response format may vary, handle different structures
  //     if (
  //       response?.success &&
  //       response?.sprints &&
  //       Array.isArray(response.sprints)
  //     ) {
  //       return response.sprints.map((sprint: any) => ({
  //         value: sprint.id,
  //         label:
  //           sprint.sprintname ??
  //           sprint.name ??
  //           sprint.sprint_name ??
  //           String(sprint.id),
  //       }));
  //     }

  //     // Fallback: Handle if response is directly an array
  //     if (Array.isArray(response)) {
  //       return response.map((sprint: any) => ({
  //         value: sprint.id ?? sprint.value,
  //         label:
  //           sprint.sprintname ??
  //           sprint.name ??
  //           sprint.sprint_name ??
  //           sprint.label ??
  //           String(sprint.id),
  //       }));
  //     }

  //     // Another possible format: response with results array
  //     if (response?.results && Array.isArray(response.results)) {
  //       return response.results.map((sprint: any) => ({
  //         value: sprint.id,
  //         label:
  //           sprint.sprintname ??
  //           sprint.name ??
  //           sprint.sprint_name ??
  //           String(sprint.id),
  //       }));
  //     }

  //     return [];
  //   },
  //   enabled: !!selectedProject,
  // });

  // Use project-filtered sprints if project is selected, otherwise use all sprints
  // const sprintOptions = selectedProject
  //   ? projectSprintOptions
  //   : allSprintOptions;

  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Fetch employee data to get first_name and last_name
  const { data: employeeDataResponse } = useQuery({
    queryKey: ["employee-data", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => {
      return DynamicModuleService.getGridColItems(
        EMPLOYEE_SCHEMA_NAME,
        1,
        1000,
        {}
      );
    },
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Extract employee records
  const employeeRecords = useMemo(() => {
    if (!employeeDataResponse) {
      return [];
    }
    if (Array.isArray(employeeDataResponse)) {
      return employeeDataResponse;
    }
    if (
      employeeDataResponse.results &&
      Array.isArray(employeeDataResponse.results)
    ) {
      return employeeDataResponse.results;
    }
    return [];
  }, [employeeDataResponse]);

  // Get employee column grid data to find first_name and last_name fields
  const { data: employeeColumnGridData } = useQuery({
    queryKey: ["columnGridData", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(EMPLOYEE_SCHEMA_NAME),
  });

  // Find employee field names
  const findEmployeeFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!employeeColumnGridData?.grid) {
        return null;
      }
      for (const field of employeeColumnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(term => {
            const lowerTerm = term.toLowerCase();
            return (
              fieldName.includes(lowerTerm) ?? fieldTitle.includes(lowerTerm)
            );
          })
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [employeeColumnGridData]
  );

  const employeeFirstNameField = findEmployeeFieldName([
    "first_name",
    "firstname",
    "fname",
  ]);
  const employeeLastNameField = findEmployeeFieldName([
    "last_name",
    "lastname",
    "lname",
  ]);

  // Transform employee options to show first name + last name
  const employeeOptionsWithNames = useMemo(() => {
    if (!employeeOptions || !employeeRecords.length) {
      return employeeOptions ?? [];
    }

    return employeeOptions.map((employee: { value: string; label: string }) => {
      // Find the employee record by ID
      const employeeRecord = employeeRecords.find(
        (record: any) =>
          String(
            record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
          ) === String(employee.value)
      );

      if (employeeRecord) {
        // Get first name and last name from the record
        const firstName =
          (employeeFirstNameField && employeeRecord[employeeFirstNameField]) ??
          employeeRecord.first_name ??
          employeeRecord.firstname ??
          employeeRecord.fname ??
          "";
        const lastName =
          (employeeLastNameField && employeeRecord[employeeLastNameField]) ??
          employeeRecord.last_name ??
          employeeRecord.lastname ??
          employeeRecord.lname ??
          "";

        // Combine first name and last name
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          value: employee.value,
          label: fullName || employee.label, // Fallback to original label if names not found
        };
      }

      // Fallback to original label if employee record not found
      return employee;
    });
  }, [
    employeeOptions,
    employeeRecords,
    employeeFirstNameField,
    employeeLastNameField,
  ]);

  const { data: categoryOptions } = useQuery({
    queryKey: ["category-dropdown", TASK_CATEGORY_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(TASK_CATEGORY_SCHEMA_NAME),
    enabled: !!TASK_CATEGORY_SCHEMA_NAME,
  });

  const { data: workTypeOptions } = useQuery({
    queryKey: ["work-type-dropdown", WORK_TYPE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(WORK_TYPE_SCHEMA_NAME),
    enabled: !!WORK_TYPE_SCHEMA_NAME,
  });

  const { data: statusOptions } = useQuery({
    queryKey: ["status-dropdown", STATUS_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(STATUS_SCHEMA_NAME),
    enabled: !!STATUS_SCHEMA_NAME,
  });

  // Fetch parent tasks by project (project-root-task API)
  const { data: parent_taskOptions, isLoading: parent_tasksLoading } = useQuery(
    {
      queryKey: ["project-root-task", selectedProject],
      queryFn: async () => {
        if (!selectedProject) {
          return [];
        }
        const response = await get<any>("/pts_app/project-root-task/", {
          params: {
            project_id: selectedProject,
          },
        });

        // Format response to match dropdown format (value/label)
        // Response format: { success: {...}, tasks: [{ id, taskname }], count: number }
        if (
          response?.success &&
          response?.tasks &&
          Array.isArray(response.tasks)
        ) {
          return response.tasks.map((task: any) => ({
            value: task.id,
            label: task.taskname ?? task.name ?? String(task.id),
          }));
        }

        // Fallback: Handle if response is directly an array
        if (Array.isArray(response)) {
          return response.map((task: any) => ({
            value: task.id ?? task.value,
            label: task.taskname ?? task.name ?? task.label ?? String(task.id),
          }));
        }

        return [];
      },
      enabled: !!selectedProject,
    }
  );

  // Get column grid data to generate all columns dynamically
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", TASK_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(TASK_SCHEMA_NAME),
  });

  // Fetch task data for editing
  const { data: taskData } = useQuery({
    queryKey: ["task-data", TASK_SCHEMA_NAME, editingTaskId],
    queryFn: () => {
      if (!editingTaskId) {
        throw new Error("Task ID is required");
      }
      return DynamicModuleService.getItemById(TASK_SCHEMA_NAME, editingTaskId);
    },
    enabled: !!editingTaskId && isEditMode,
  });

  // Helper function to get project name by ID
  const getProjectNameById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return null;
      }
      return projectOptions?.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
    },
    [projectOptions]
  );

  // Helper function to get sprint name by ID
  // Uses allSprintOptions since table displays all tasks regardless of selected project
  const getSprintNameById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return null;
      }
      return allSprintOptions?.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
    },
    [allSprintOptions]
  );

  // Helper function to get employee name by ID from PTS_Emp schema
  // const getEmployeeNameById = useCallback(
  //   (id: string | number | null | undefined) => {
  //     if (!id) {
  //       return null;
  //     }

  //     // First try to find in employeeOptionsWithNames
  //     const employeeOption = employeeOptionsWithNames?.find(
  //       (item: { value: string; label: string }) =>
  //         String(item.value) === String(id)
  //     );

  //     if (employeeOption) {
  //       return employeeOption;
  //     }

  //     // If not found in options, try to find directly in employeeRecords from PTS_Emp
  //     const employeeRecord = employeeRecords.find(
  //       (record: any) =>
  //         String(
  //           record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
  //         ) === String(id)
  //     );

  //     if (employeeRecord) {
  //       // Get first name and last name from the record
  //       const firstName =
  //         (employeeFirstNameField && employeeRecord[employeeFirstNameField]) ??
  //         employeeRecord.first_name ??
  //         employeeRecord.firstname ??
  //         employeeRecord.fname ??
  //         "";
  //       const lastName =
  //         (employeeLastNameField && employeeRecord[employeeLastNameField]) ??
  //         employeeRecord.last_name ??
  //         employeeRecord.lastname ??
  //         employeeRecord.lname ??
  //         "";

  //       // Combine first name and last name
  //       const fullName = `${firstName} ${lastName}`.trim();

  //       return {
  //         value: String(id),
  //         label: fullName || String(id), // Fallback to ID if name not found
  //       };
  //     }

  //     // Return null if not found
  //     return null;
  //   },
  //   [
  //     employeeOptionsWithNames,
  //     employeeRecords,
  //     employeeFirstNameField,
  //     employeeLastNameField,
  //   ]
  // );

  // Helper function to format date and time
  const formatDateTime = useCallback(
    (dateString: string | null | undefined) => {
      if (!dateString) {
        return "-";
      }
      try {
        const date = new Date(dateString);
        // Format: "Dec 28, 10:30 AM" or similar
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, "0");
        return `${month} ${day}, ${displayHours}:${displayMinutes} ${ampm}`;
      } catch {
        return dateString;
      }
    },
    []
  );

  // Populate form when task data is loaded for editing
  useEffect(() => {
    if (taskData && isEditMode) {
      // Find field names from schema
      const findFieldName = (searchTerms: string[]) => {
        if (!columnGridData?.grid) {
          return null;
        }
        for (const field of columnGridData.grid) {
          const fieldName = field.field_name.toLowerCase();
          const fieldTitle = field.field_title?.toLowerCase() ?? "";
          if (
            searchTerms.some(
              term =>
                fieldName.includes(term.toLowerCase()) ??
                fieldTitle.includes(term.toLowerCase())
            )
          ) {
            return field.field_name; // Return actual field name from schema
          }
        }
        return null;
      };

      const taskNameField = findFieldName([
        "taskname",
        "task_name",
        "name",
        "task",
      ]);
      const projectField = findFieldName(["project", "project_id"]);
      const sprintField = findFieldName(["sprint", "sprint_id"]);
      const workTypeField = findFieldName([
        "work_type",
        "work_type_id",
        "work_item",
      ]);
      const categoryField = findFieldName([
        "taskcategory",
        "category",
        "task_category",
      ]);
      const hoursField = findFieldName([
        "taskestimatedhours",
        "estimated_hours",
        "est_hours",
        "hours",
      ]);
      const parent_taskField = findFieldName([
        "parent_task",
        "parent_task",
        "parent",
      ]);
      const statusField = findFieldName(["status"]);
      const emplistField = findFieldName([
        "emplist",
        "employee_list",
        "employees",
      ]);
      const descriptionField = findFieldName([
        "description",
        "desc",
        "task_description",
      ]);
      const attachmentField = findFieldName([
        "attachment",
        "attachments",
        "task_attachment",
        "file",
        "document",
      ]);

      const formValues: any = {};

      // Set task name
      if (taskNameField && taskData[taskNameField]) {
        formValues.task_name = taskData[taskNameField];
      } else if (taskData.taskname || taskData.task_name || taskData.name) {
        formValues.task_name =
          taskData.taskname ?? taskData.task_name ?? taskData.name;
      }

      // Set project
      if (projectField && taskData[projectField]) {
        formValues.project = taskData[projectField];
        setSelectedProject(taskData[projectField]);
      } else if (taskData.project_id || taskData.project) {
        const projectId = taskData.project_id ?? taskData.project;
        formValues.project = projectId;
        setSelectedProject(projectId);
      }

      // Set sprint
      if (sprintField && taskData[sprintField]) {
        formValues.sprint = taskData[sprintField];
      } else if (taskData.sprint_id || taskData.sprint) {
        formValues.sprint = taskData.sprint_id ?? taskData.sprint;
      }

      // Set category
      if (categoryField && taskData[categoryField]) {
        formValues.category = taskData[categoryField];
      } else if (taskData.taskcategory || taskData.category) {
        formValues.category = taskData.taskcategory ?? taskData.category;
      }

      // Set hours
      if (hoursField && taskData[hoursField]) {
        formValues.est_hours = taskData[hoursField];
      } else if (
        taskData.taskestimatedhours ||
        taskData.est_hours ||
        taskData.estimated_hours
      ) {
        formValues.est_hours =
          taskData.taskestimatedhours ??
          taskData.est_hours ??
          taskData.estimated_hours;
      }

      // Set parent task
      if (parent_taskField && taskData[parent_taskField]) {
        formValues.parent_task = taskData[parent_taskField];
      } else if (
        taskData.parent_task ||
        taskData.parent_task ||
        taskData.parent
      ) {
        formValues.parent_task =
          taskData.parent_task ?? taskData.parent_task ?? taskData.parent;
      }

      // Set status
      if (statusField && taskData[statusField]) {
        formValues.status = taskData[statusField];
      } else if (taskData.status) {
        formValues.status = taskData.status;
      }

      // Set work type / work item
      if (workTypeField && taskData[workTypeField]) {
        formValues.work_type = taskData[workTypeField];
      } else if (
        taskData.work_type_id ??
        taskData.work_type ??
        taskData.work_item
      ) {
        formValues.work_type =
          taskData.work_type_id ?? taskData.work_type ?? taskData.work_item;
      }

      // Set assigned employees from emplist
      const emplist =
        (emplistField && taskData[emplistField]) ?? taskData.emplist ?? [];
      if (Array.isArray(emplist) && emplist.length > 0) {
        const employees = emplist.map((emp: any) => {
          const employeeId = emp?.member;
          const employee = employeeOptions?.find(
            (e: { value: string; label: string }) =>
              String(e.value) === String(employeeId)
          );
          return {
            id: employeeId,
            name: employee?.label ?? employeeId,
          };
        });
        setAssignedEmployees(employees);
        // Set selected employee IDs for multi-select
        setSelectedEmployeeIds(employees.map(emp => String(emp.id)));
      }

      // Description (plain text; require defined values so undefined is not coerced to "undefined")
      if (
        descriptionField &&
        taskData[descriptionField] !== undefined &&
        taskData[descriptionField] !== null
      ) {
        const d = taskData[descriptionField];
        formValues.description = typeof d === "string" ? d : String(d);
      } else if (
        taskData.description !== undefined &&
        taskData.description !== null
      ) {
        const d = taskData.description;
        formValues.description = typeof d === "string" ? d : String(d);
      } else if (
        taskData.task_description !== undefined &&
        taskData.task_description !== null
      ) {
        const d = taskData.task_description;
        formValues.description = typeof d === "string" ? d : String(d);
      } else {
        formValues.description = "";
      }

      // Attachment (file UUID)
      let rawAtt: unknown =
        (attachmentField && taskData[attachmentField]) ??
        taskData.attachment ??
        taskData.attachments;
      if (Array.isArray(rawAtt) && rawAtt.length > 0) {
        rawAtt = rawAtt[0];
      }
      if (rawAtt && typeof rawAtt === "object" && "id" in (rawAtt as object)) {
        rawAtt = (rawAtt as { id: string }).id;
      }
      if (
        typeof rawAtt === "string" &&
        rawAtt.length > 0 &&
        rawAtt !== "undefined"
      ) {
        formValues.attachment = rawAtt;
      }

      form.setFieldsValue(formValues);
    }
  }, [taskData, isEditMode, form, columnGridData, employeeOptions]);

  // Generate all columns dynamically
  const columns: ProColumns<any>[] = useMemo(() => {
    const handleDelete = async (id: string | undefined) => {
      if (!id) {
        notify.error("Task ID is missing");
        return;
      }
      try {
        const response = await DynamicModuleService.deleteItem(
          TASK_SCHEMA_NAME,
          String(id)
        );
        if (response) {
          queryClient.invalidateQueries({
            queryKey: ["moduleData", TASK_SCHEMA_NAME],
          });
        }
      } catch (error: any) {
        notify.error(error?.message ?? "Failed to delete task");
      }
    };

    const handleEdit = (id: string | undefined) => {
      if (!id) {
        notify.error("Task ID is missing");
        return;
      }
      setEditingTaskId(String(id));
      setIsEditMode(true);
      scrollToTaskForm();
    };

    if (!columnGridData?.grid) {
      return [];
    }

    // Find field names from schema
    const findFieldName = (searchTerms: string[]) => {
      for (const field of columnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(
            term =>
              fieldName.includes(term.toLowerCase()) ??
              fieldTitle.includes(term.toLowerCase())
          )
        ) {
          return field.field_name; // Return actual field name from schema (not lowercase)
        }
      }
      return null;
    };

    const taskNameField = findFieldName([
      "taskname",
      "task_name",
      "name",
      "task",
    ]);
    const projectField = findFieldName([
      "project",
      "project_id",
      "project_name",
    ]);
    const sprintField = findFieldName(["sprint", "sprint_id", "sprint_name"]);
    const workTypeField = findFieldName([
      "work_type",
      "work_type_id",
      "work_item",
    ]);
    const categoryField = findFieldName(["category", "task_category"]);
    const hoursField = findFieldName([
      "hours",
      "est_hours",
      "estimated_hours",
      "taskestimatedhours",
      "hours_estimated",
    ]);
    const actualHoursField = findFieldName([
      "actualhours",
      "actual_hours",
      "taskactualhours",
      "hours_actual",
    ]);
    const varianceField = findFieldName([
      "variance",
      "hours_variance",
      "taskvariance",
    ]);

    // Build custom columns matching the image design
    const customColumns: ProColumns<any>[] = [
      {
        title: "Task Name",
        dataIndex: taskNameField ?? undefined,
        key: "task_name",
        width: 300,
        render: (_: any, record: any) => {
          const taskName =
            (taskNameField && record[taskNameField]) ??
            record.taskname ??
            record.task_name ??
            record.name ??
            "-";
          const subtasks = record.subtask ?? record.children ?? [];
          const hasSubtasks = subtasks.length > 0;
          // Check if this is a subtask (has parent_task)
          const isSubtask = record.parent_task ?? record.parenttask;

          // Check if task was edited: created_by != updated_by || created_on != updated_on
          const createdBy = record.created_by;
          const updatedBy = record.updated_by;
          const createdOn = record.created_on;
          const updatedOn = record.updated_on;
          const isEdited = createdBy !== updatedBy || createdOn !== updatedOn;

          const formattedDate = isEdited ? formatDateTime(updatedOn) : "";

          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                {isSubtask && (
                  <span
                    className="ml-1 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center text-[10px] font-bold text-gray-500"
                    style={{
                      textShadow: `
                        -0.5px 0 0 rgba(255, 140, 0, 0.6),
                        0.5px 0 0 rgba(100, 200, 255, 0.6),
                        0 -0.5px 0 rgba(100, 200, 255, 0.6)
                      `,
                    }}
                  >
                    L
                  </span>
                )}
                <span
                  className="cursor-pointer font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  role="button"
                  tabIndex={0}
                  title="Edit Task"
                  onClick={event => {
                    event.stopPropagation();
                    const recordId =
                      record?.id ?? record?.ID ?? record?.Id ?? record?._id;
                    handleEdit(recordId);
                  }}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.stopPropagation();
                      const recordId =
                        record?.id ?? record?.ID ?? record?.Id ?? record?._id;
                      handleEdit(recordId);
                    }
                  }}
                >
                  {taskName}
                </span>
                {hasSubtasks && !isSubtask && (
                  <span className="text-primary inline-flex items-center rounded-full border border-gray-300 px-2 py-0.5 text-xs font-medium">
                    {subtasks.length} subtasks
                  </span>
                )}
              </div>
              {isEdited && (
                <Tooltip
                  title={
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-800" />
                        <EmployeeTooltipContent userId={updatedBy} />
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-gray-800" />
                        <span className="text-gray-700">{formattedDate}</span>
                      </div>
                    </div>
                  }
                  placement="bottom"
                  overlayInnerStyle={{
                    backgroundColor: "#ffffff", // White background
                    color: "#374151", // Dark gray text
                    borderRadius: "8px",
                    border: "1px solid #e0e6eb",
                    padding: "8px 12px",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  <div className="flex cursor-pointer items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                    <Pencil size={12} />
                    <span>Edited</span>
                  </div>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Project",
        dataIndex: projectField ?? undefined,
        key: "project",
        render: (_: any, record: any) => {
          const projectId =
            (projectField && record[projectField]) ??
            record.project_id ??
            record.project;
          if (!projectId) {
            return "-";
          }
          const project = getProjectNameById(projectId);
          return project?.label ?? projectId;
        },
      },
      {
        title: "Sprint",
        dataIndex: sprintField ?? undefined,
        key: "sprint",
        render: (_: any, record: any) => {
          const sprintId =
            (sprintField && record[sprintField]) ??
            record.sprint_id ??
            record.sprint;
          if (!sprintId) {
            return "-";
          }
          const sprint = getSprintNameById(sprintId);
          return sprint?.label ?? sprintId;
        },
      },
      {
        title: "Work Area",
        dataIndex: categoryField ?? undefined,
        key: "category",
        render: (_: any, record: any) => {
          const categoryId =
            (categoryField && record[categoryField]) ??
            record.category ??
            record.task_category ??
            record.taskcategory;
          if (!categoryId) {
            return "-";
          }
          // Try to find category name from options
          const category = categoryOptions?.find(
            (item: { value: string; label: string }) =>
              String(item.value) === String(categoryId)
          );
          return category?.label ?? categoryId;
        },
      },
      {
        title: "Work Type",
        dataIndex: workTypeField ?? undefined,
        key: "work_type",
        render: (_: any, record: any) => {
          const workTypeId =
            (workTypeField && record[workTypeField]) ??
            record.work_type_id ??
            record.work_type ??
            record.work_item;
          if (!workTypeId) {
            return "-";
          }
          const option = workTypeOptions?.find(
            (item: {
              value?: string;
              label?: string;
              id?: string;
              name?: string;
            }) => String(item?.value ?? item?.id ?? "") === String(workTypeId)
          );
          return (option?.label ?? option?.name ?? workTypeId) as string;
        },
      },
      {
        title: "Est. Hours",
        dataIndex: hoursField ?? undefined,
        key: "hours",
        render: (_: any, record: any) => {
          const hours =
            (hoursField && record[hoursField]) ??
            record.hours ??
            record.taskestimatedhours ??
            record.est_hours ??
            record.estimated_hours ??
            0;
          return typeof hours === "number" ? hours : (hours ?? "0");
        },
      },
      {
        title: "Actual Hours",
        dataIndex: actualHoursField ?? undefined,
        key: "actualhours",
        render: (_: any, record: any) => {
          const actualHours =
            (actualHoursField && record[actualHoursField]) ??
            record.actualhours ??
            record.actual_hours ??
            0;
          return typeof actualHours === "number"
            ? actualHours
            : (actualHours ?? "0");
        },
      },
      {
        title: "Variance",
        dataIndex: varianceField ?? undefined,
        key: "variance",
        render: (_: any, record: any) => {
          const variance =
            (varianceField && record[varianceField]) ??
            record.variance ??
            record.hours_variance ??
            0;

          // Convert to number for comparison
          const varianceNum =
            typeof variance === "number"
              ? variance
              : typeof variance === "string"
                ? parseFloat(variance)
                : 0;

          // Handle null, undefined, or NaN
          if (
            varianceNum === null ||
            varianceNum === undefined ||
            isNaN(varianceNum)
          ) {
            return <span className="text-gray-900">-</span>;
          }

          // Format the display value
          const displayValue =
            typeof variance === "number"
              ? variance
              : typeof variance === "string"
                ? variance
                : String(varianceNum);

          // Apply color based on value
          if (varianceNum < 0) {
            // Negative - red text
            return (
              <span className="font-normal text-red-600">{displayValue}</span>
            );
          } else if (varianceNum > 0) {
            // Positive - green text
            return (
              <span className="font-normal text-green-600">{displayValue}</span>
            );
          } else {
            // Zero - normal text
            return <span className="text-gray-900">{displayValue}</span>;
          }
        },
      },
    ];

    return [
      ...customColumns,
      {
        title: "Actions",
        width: 120,
        valueType: "option",
        key: "actions",
        fixed: "right",
        render: (_: any, record: any) => {
          const recordId =
            record?.id ?? record?.ID ?? record?.Id ?? record?._id;

          return [
            <CustomButton
              key="edit"
              icon={<Edit2 size={16} />}
              type="text"
              onClick={() => {
                handleEdit(recordId);
              }}
              title="Edit Task"
            />,
            <Popconfirm
              key="delete"
              title="Are you sure you want to delete this task?"
              onConfirm={() => {
                handleDelete(recordId);
              }}
              okText="Yes"
              cancelText="No"
            >
              <CustomButton
                danger
                icon={<Trash size={16} />}
                type="text"
                title="Delete Task"
              />
            </Popconfirm>,
          ];
        },
      },
    ];
  }, [
    columnGridData,
    queryClient,
    getProjectNameById,
    getSprintNameById,
    categoryOptions,
    workTypeOptions,
    formatDateTime,
    scrollToTaskForm,
  ]);

  // Build filter object
  const filter = useMemo(() => {
    const filterObj: any = {
      is_pts_tsk: true, // Always include this parameter for task list API calls
    };

    // Use general search parameter to search across all fields
    if (searchQuery?.trim()) {
      const searchTerm = searchQuery.trim();
      // Use taskname__ilike with % for partial matching search
      filterObj.taskname__ilike = `%${searchTerm}%`;
    }

    if (sprintFilter && sprintFilter !== "all") {
      filterObj.sprint = sprintFilter;
    }
    if (statusFilter && statusFilter !== "all") {
      filterObj.status__eq = statusFilter;
    }
    if (projectFilter && projectFilter !== "all") {
      filterObj.project__eq = projectFilter;
    }
    if (assigneeFilter && assigneeFilter !== "all") {
      filterObj.assignees = assigneeFilter;
    }

    return filterObj;
  }, [searchQuery, sprintFilter, statusFilter, projectFilter, assigneeFilter]);

  // Handle employee selection change (multi-select) — keep full selection so update sends all selected employees
  const handleEmployeeChange = (selectedIds: string[]) => {
    setSelectedEmployeeIds(selectedIds);

    // Update assigned employees based on selected IDs (full list, not only new ones)
    const employees = selectedIds
      .map(id => {
        const employee = employeeOptions?.find(
          (emp: { value: string; label: string }) => emp.value === id
        );
        return employee ? { id: employee.value, name: employee.label } : null;
      })
      .filter((emp): emp is { id: string; name: string } => emp !== null);

    setAssignedEmployees(employees);
    form.setFieldsValue({ assigned_employees: selectedIds });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditingTaskId(null);
    setAssignedEmployees([]);
    setSelectedEmployeeIds([]);
    setSelectedProject(null);
    form.resetFields();
  };

  // Handle form submission (both create and update)
  const handleSubmitTask = async (
    values: any,
    options?: { keepForm?: boolean }
  ) => {
    const keepForm = Boolean(options?.keepForm);

    try {
      // Validate required fields
      if (!values.project) {
        notify.error("Please select a project");
        return;
      }
      const taskNameTrimmed = String(values.task_name ?? "").trim();
      if (!taskNameTrimmed) {
        notify.error("Please enter task name");
        return;
      }
      if (taskNameTrimmed.replace(/\s/g, "").length > 50) {
        notify.error("Task name must be 50 characters or less");
        return;
      }
      // if (!values.category) {
      //   notify.error("Please select a category");
      //   return;
      // }
      // Use selectedEmployeeIds (current selection in Assign Employees field) so update sends all selected, not just new ones
      const employeeIdsToSend =
        selectedEmployeeIds.length > 0
          ? selectedEmployeeIds
          : assignedEmployees.map(e => String(e.id));
      if (employeeIdsToSend.length === 0) {
        notify.error("Please assign at least one employee");
        return;
      }

      setIsSubmitting(true);

      // Find field names from schema to ensure correct mapping
      const findFieldName = (searchTerms: string[]) => {
        if (!columnGridData?.grid) {
          return null;
        }
        for (const field of columnGridData.grid) {
          const fieldName = field.field_name.toLowerCase();
          const fieldTitle = field.field_title?.toLowerCase() ?? "";
          if (
            searchTerms.some(
              term =>
                fieldName.includes(term.toLowerCase()) ??
                fieldTitle.includes(term.toLowerCase())
            )
          ) {
            return field.field_name; // Return actual field name from schema
          }
        }
        return null;
      };

      // Get actual schema field names
      const projectField =
        findFieldName(["project", "project_id"]) ?? "project";
      const sprintField = findFieldName(["sprint", "sprint_id"]) ?? "sprint";
      const taskNameField =
        findFieldName(["taskname", "task_name", "name", "task"]) ?? "taskname";
      const categoryField =
        findFieldName(["taskcategory", "category", "task_category"]) ??
        "taskcategory";
      const hoursField =
        findFieldName([
          "taskestimatedhours",
          "estimated_hours",
          "est_hours",
          "hours",
        ]) ?? "taskestimatedhours";
      const parent_taskField =
        findFieldName(["parent_task", "parent_task", "parent"]) ??
        "parent_task";
      const statusField = findFieldName(["status"]) ?? "status";
      const workTypeField =
        findFieldName(["work_type", "work_type_id", "work_item"]) ??
        "work_type";
      const emplistField =
        findFieldName([
          "emplist",
          "employee_list",
          "employees",
          "assigned_employees",
        ]) ?? "emplist";
      const descriptionField =
        findFieldName(["description", "desc", "task_description"]) ??
        "description";
      const attachmentField =
        findFieldName([
          "attachment",
          "attachments",
          "task_attachment",
          "file",
          "document",
        ]) ?? "attachment";

      // Format payload according to the required structure
      const now = new Date().toISOString();

      // Generate index (using timestamp + random number for uniqueness)
      const generateIndex = () => {
        return Date.now() * 1000 + Math.floor(Math.random() * 1000);
      };

      // Format emplist from all currently selected employees (so edit/update sends full list, not only new ones)
      const emplist = employeeIdsToSend.map(id => ({
        member: id,
        index: generateIndex(),
        created_on: now,
        updated_on: now,
      }));

      // Prepare payload with schema field names
      const payload: any = {};
      payload[projectField] = values.project;
      payload[taskNameField] = taskNameTrimmed;
      // For edit: use sprint id from fetched task data; for create: use form value
      const sprintValue =
        isEditMode && taskData
          ? ((sprintField && taskData[sprintField]) ??
            taskData.sprint_id ??
            taskData.sprint ??
            null)
          : (values.sprint ?? null);
      payload[sprintField] = sprintValue;
      payload[categoryField] = values.category;
      // est_hours is now required, so always include it
      payload[hoursField] = values.est_hours ? Number(values.est_hours) : 0;
      if (values.parent_task) {
        payload[parent_taskField] = values.parent_task;
      }
      // status is now required, so always include it
      payload[statusField] = values.status;
      // Work Type - always include (use schema field name or fallback "work_type")
      payload[workTypeField] = values.work_type ?? null;
      payload[emplistField] = emplist;
      // Optional: only send description / attachment when there is real data (not undefined / literal "undefined")
      const trimmedDescription =
        values.description !== undefined && values.description !== null
          ? String(values.description).trim()
          : "";
      if (trimmedDescription !== "" && trimmedDescription !== "undefined") {
        payload[descriptionField] = trimmedDescription;
      }
      const attachmentStr =
        values.attachment !== undefined && values.attachment !== null
          ? String(values.attachment).trim()
          : "";
      if (attachmentStr !== "" && attachmentStr !== "undefined") {
        payload[attachmentField] = attachmentStr;
      }

      // Log payload for debugging
      console.log("Task payload:", JSON.stringify(payload, null, 2));

      // Validate estimated hours with project before create/update (est_hours is required)
      if (values.est_hours && values.project) {
        try {
          const checkParams: Record<string, string | number> = {
            project_id: values.project,
            estimated_hours: values.est_hours,
          };
          // Create: when parent task is selected, pass parent task id as task_id (task_name not passed)
          if (!isEditMode && values.parent_task) {
            checkParams.task_id = values.parent_task;
          }
          // Update: pass the main task id as update_task_id; when parent task is selected, also pass task_id
          if (isEditMode && editingTaskId) {
            checkParams.update_task_id = editingTaskId;
            if (values.parent_task) {
              checkParams.task_id = values.parent_task;
            }
          }
          // const validationResponse = await get<any>(
          //   "/pts_app/check-project-estimated/",
          //   {
          //     params: checkParams,
          //   }
          // );

          // // Check if validation failed - response structure: { data: { is_valid: boolean, message: string } }
          // const validationData = validationResponse?.data ?? validationResponse;
          // if (validationData?.is_valid === false) {
          //   const errorMessage =
          //     validationData?.message ??
          //     validationResponse?.message ??
          //     validationResponse?.error ??
          //     "Estimated hours validation failed for this project";

          //   // Set form field error to highlight the estimated hours field with red border
          //   form.setFields([
          //     {
          //       name: "est_hours",
          //       errors: [errorMessage],
          //     },
          //   ]);

          //   notify.error(errorMessage);
          //   setIsSubmitting(false);
          //   return;
          // }
        } catch (validationError: any) {
          console.error("Validation API error:", validationError);
          // If validation API fails, show error and stop
          const errorMessage =
            validationError?.response?.data?.data?.message ??
            validationError?.response?.data?.message ??
            validationError?.response?.data?.error ??
            validationError?.message ??
            "Failed to validate estimated hours";

          // Set form field error to highlight the estimated hours field with red border
          form.setFields([
            {
              name: "est_hours",
              errors: [errorMessage],
            },
          ]);

          notify.error(errorMessage);
          setIsSubmitting(false);
          return;
        }
      }

      if (isEditMode && editingTaskId) {
        // Update existing task
        const response = await DynamicModuleService.updateItem(
          TASK_SCHEMA_NAME,
          editingTaskId,
          payload
        );

        if (response) {
          // notify.success("Task updated successfully");
          handleCancelEdit();
          queryClient.invalidateQueries({
            queryKey: ["moduleData", TASK_SCHEMA_NAME],
          });
        }
      } else {
        // Create new task
        const response = await DynamicModuleService.createItem(
          TASK_SCHEMA_NAME,
          payload
        );

        if (response) {
          // notify.success("Task created successfully");
          queryClient.invalidateQueries({
            queryKey: ["moduleData", TASK_SCHEMA_NAME],
          });
          if (!keepForm) {
            form.resetFields();
            setAssignedEmployees([]);
            setSelectedEmployeeIds([]);
            setSelectedProject(null);
          }
        }
      }
    } catch (error: any) {
      console.error("Task submission error:", error);
      console.error("Error response:", error?.response?.data);

      // Show more detailed error message
      const errorMessage =
        error?.response?.data?.detail ??
        error?.response?.data?.error ??
        error?.message ??
        `Failed to ${isEditMode ? "update" : "create"} task`;

      notify.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toolBarRender = () => {
    return [
      <div key="search" className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          type="text"
          placeholder="Search by task name..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-64 pl-10"
        />
      </div>,
    ];
  };

  return (
    <>
      <style>{`
        .task-form-select .ant-select-selector {
          background-color: #fafafa !important; 
          border-radius: 0.5rem !important;
          border-color: #e5e7eb !important;
        }
        .task-form-radio .ant-radio-wrapper {
          margin-right: 1rem;
        }
        .task-form-radio .ant-radio-inner {
          border-color: #d1d5db;
        }
        .task-form-attachment .ant-upload-list-item {
          border-radius: 0.5rem;
        }
        .task-form-attachment .task-attachment-thumb .ant-image,
        .task-form-attachment .task-attachment-thumb .ant-image-img {
          width: 100%;
          height: 100%;
        }
        .task-form-attachment .task-attachment-thumb .ant-image-img {
          object-fit: cover;
        }
        .task-create-form .ant-form-item-label > label {
          color: #374151;
          font-size: 0.8125rem;
          font-weight: 500;
        }
        .task-create-form .task-form-section-details .ant-form-item {
          margin-bottom: 0;
        }
      `}</style>
      <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
        {/* Page Header */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
              Tasks
            </h1>
            <p className="text-sm text-[#627384]">
              Manage tasks and sprint assignments
            </p>
          </div>
        </div>

        {/* Create New Task Section */}
        <Card
          ref={taskFormCardRef}
          className={`scroll-mt-20 border bg-white transition-[box-shadow,border-color] duration-500 ${
            isFormHighlighted
              ? "border-primary shadow-primary/25 ring-primary/40 shadow-lg ring-2"
              : "border-gray-200"
          }`}
        >
          <CardContent className="p-3 md:p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-[#111827]">
                {isEditMode ? (
                  <>
                    <Edit2 className="h-5 w-5" />
                    Edit Task
                  </>
                ) : (
                  <>
                    <Plus className="h-5 w-5" />
                    Create New Task
                  </>
                )}
              </h2>
              {/* {isEditMode && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
              )} */}
            </div>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmitTask}
              className="task-create-form space-y-5 lg:space-y-6"
            >
              {/* Primary fields — four equal columns on large screens */}
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4 lg:grid-cols-4 lg:gap-5">
                <Form.Item
                  name="project"
                  label={<span>Project</span>}
                  rules={[
                    { required: true, message: "Please select a project" },
                  ]}
                >
                  <Select
                    placeholder="Select project"
                    showSearch
                    allowClear
                    className="task-form-select"
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    options={projectOptions?.map(
                      (project: { value: string; label: string }) => ({
                        value: project.value,
                        label: project.label,
                      })
                    )}
                    onChange={value => {
                      setSelectedProject(value);
                      // Clear parent task and sprint when project changes
                      form.setFieldValue("parent_task", undefined);
                      form.setFieldValue("sprint", undefined);
                    }}
                  />
                </Form.Item>

                <Form.Item name="parent_task" label={<span>Parent Task</span>}>
                  <Select
                    allowClear
                    placeholder={
                      selectedProject ? "Select task" : "Select a project first"
                    }
                    showSearch
                    disabled={!selectedProject}
                    loading={parent_tasksLoading}
                    className="task-form-select"
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    options={parent_taskOptions
                      ?.filter(
                        (task: { value: string; label: string }) =>
                          !isEditMode ||
                          String(task.value) !== String(editingTaskId)
                      )
                      .map((task: { value: string; label: string }) => ({
                        value: task.value,
                        label: task.label,
                      }))}
                  />
                </Form.Item>

                {/* <Form.Item
                  name="sprint"
                  label={<span>Sprint</span>}
                  className="md:col-span-1"
                >
                  <Select
                    allowClear
                    placeholder={
                      selectedProject
                        ? "Select sprint"
                        : "Select a project first"
                    }
                    showSearch
                    disabled={!selectedProject}
                    loading={selectedProject ? projectSprintsLoading : false}
                    className="task-form-select"
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    options={sprintOptions?.map(
                      (sprint: { value: string; label: string }) => ({
                        value: sprint.value,
                        label: sprint.label,
                      })
                    )}
                  />
                </Form.Item> */}

                <Form.Item
                  name="category"
                  label={<span>Work Area</span>}
                  // rules={[
                  //   { required: true, message: "Please select a category" },
                  // ]}
                >
                  <Select
                    allowClear
                    placeholder="Select category"
                    showSearch
                    className="task-form-select"
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    options={categoryOptions?.map(
                      (category: { value: string; label: string }) => ({
                        value: category.value,
                        label: category.label,
                      })
                    )}
                  />
                </Form.Item>

                <Form.Item
                  name="status"
                  label={<span>Status</span>}
                  rules={[
                    { required: true, message: "Please select a status" },
                  ]}
                >
                  <Select
                    allowClear
                    placeholder="Select status"
                    showSearch
                    className="task-form-select"
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    options={statusOptions?.map(
                      (status: { value: string; label: string }) => ({
                        value: status.value,
                        label: status.label,
                      })
                    )}
                  />
                </Form.Item>
              </div>

              {/* Task details — three equal columns (full width, no stray empty grid cell) */}
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4 lg:grid-cols-3 lg:gap-5">
                <Form.Item
                  name="task_name"
                  label={<span>Task Name</span>}
                  rules={[
                    {
                      validator: async (_, value) => {
                        const s =
                          value === undefined || value === null
                            ? ""
                            : String(value);
                        const trimmed = s.trim();
                        if (!trimmed) {
                          return Promise.reject(
                            new Error("Please enter task name")
                          );
                        }
                        if (trimmed.replace(/\s/g, "").length > 50) {
                          return Promise.reject(
                            new Error("Task name must be 50 characters or less")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter task name"
                    className="h-[2rem] rounded-lg border-gray-200 bg-[#fafafa]"
                  />
                </Form.Item>

                <Form.Item
                  name="est_hours"
                  label={<span>Estimated Hours</span>}
                  rules={[
                    { required: true, message: "Please enter estimated hours" },
                  ]}
                >
                  <Input
                    type="number"
                    placeholder="Enter estimated hours"
                    className="h-[2rem] rounded-lg border-gray-200 bg-[#fafafa]"
                  />
                </Form.Item>

                <Form.Item
                  name="work_type"
                  label={<span>Work Type</span>}
                  rules={[
                    { required: true, message: "Please select a work type" },
                  ]}
                >
                  <Select
                    allowClear
                    placeholder="Select work type"
                    showSearch
                    className="task-form-select"
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    options={workTypeOptions?.map(
                      (workType: { value: string; label: string }) => ({
                        value: workType.value,
                        label: workType.label,
                      })
                    )}
                  />
                </Form.Item>
              </div>

              {/* Description (left) + attachment (right) — two columns from lg; stack on small screens */}
              <div className="">
                <div className="grid grid-cols-1 gap-2 md:gap-5 lg:grid-cols-2 lg:items-start lg:gap-6">
                  <Form.Item
                    name="description"
                    label={<span>Description (optional)</span>}
                    className="mb-0 min-w-0"
                  >
                    <AntdInput.TextArea
                      rows={3}
                      placeholder="Enter task description"
                      className="focus:border-primary min-h-[140px] rounded-lg border-gray-200 lg:min-h-[200px]"
                      style={{ resize: "vertical" }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="attachment"
                    label={<span>Attachment (optional)</span>}
                    className="mb-0 min-w-0"
                  >
                    <TaskAttachmentField />
                  </Form.Item>
                </div>
              </div>

              {/* Assign Employees Section */}
              <Form.Item
                name="assigned_employees"
                label={<span>Assign Employees</span>}
                rules={[
                  {
                    required: true,
                    validator: () => {
                      const count =
                        selectedEmployeeIds.length || assignedEmployees.length;
                      if (count === 0) {
                        return Promise.reject(
                          new Error("Please assign at least one employee")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <div className="w-[50%]">
                  <Select
                    allowClear
                    mode="multiple"
                    placeholder="Select employees"
                    showSearch
                    value={selectedEmployeeIds}
                    onChange={handleEmployeeChange}
                    className="task-form-select w-full"
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    options={employeeOptionsWithNames?.map(
                      (employee: { value: string; label: string }) => ({
                        value: employee.value,
                        label: employee.label,
                      })
                    )}
                  />
                </div>
              </Form.Item>

              <div className="flex justify-end gap-3">
                {isEditMode && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                )}
                {!isEditMode && (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSubmitting}
                    className="flex items-center gap-2"
                    onClick={() => {
                      void form
                        .validateFields()
                        .then(values =>
                          handleSubmitTask(values, { keepForm: true })
                        )
                        .catch(() => {
                          /* validation errors shown on fields */
                        });
                    }}
                  >
                    <Copy className="h-4 w-4" />
                    {isSubmitting ? "Creating..." : "Copy to Create"}
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
                >
                  {isEditMode ? (
                    <>
                      <Edit2 className="h-4 w-4" />
                      {isSubmitting ? "Updating..." : "Update Task"}
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      {isSubmitting ? "Creating..." : "Create Task"}
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </CardContent>
        </Card>

        {/* Tasks by Sprint Section */}
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-3 md:p-4">
            <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <h2 className="text-lg font-semibold text-[#111827]">
                Tasks by Sprint
              </h2>
              <div className="flex flex-wrap gap-3">
                <Select
                  id="sprint"
                  allowClear
                  value={sprintFilter}
                  onChange={setSprintFilter}
                  placeholder="All Sprints"
                  className="w-40"
                  showSearch
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={[
                    { value: "all", label: "All Sprints" },
                    ...(allSprintOptions?.map(
                      (sprint: { value: string; label: string }) => ({
                        value: sprint.value,
                        label: sprint.label,
                      })
                    ) ?? []),
                  ]}
                />
                <Select
                  id="status"
                  allowClear
                  value={statusFilter}
                  onChange={setStatusFilter}
                  placeholder="All Statuses"
                  className="w-40"
                  showSearch
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={[
                    { value: "all", label: "All Statuses" },
                    ...(statusOptions?.map(
                      (status: { value: string; label: string }) => ({
                        value: status.value,
                        label: status.label,
                      })
                    ) ?? []),
                  ]}
                />
                <Select
                  id="project"
                  allowClear
                  value={projectFilter}
                  onChange={setProjectFilter}
                  placeholder="All Projects"
                  className="w-40"
                  showSearch
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={[
                    { value: "all", label: "All Projects" },
                    ...(projectOptions?.map(
                      (project: { value: string; label: string }) => ({
                        value: project.value,
                        label: project.label,
                      })
                    ) ?? []),
                  ]}
                />
                <Select
                  id="employee"
                  allowClear
                  value={assigneeFilter}
                  onChange={setAssigneeFilter}
                  placeholder="Select employee"
                  className="w-40"
                  showSearch
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={[
                    { value: "all", label: "All Employees" },
                    ...(employeeOptionsWithNames?.map(
                      (employee: { value: string; label: string }) => ({
                        value: employee.value,
                        label: employee.label,
                      })
                    ) ?? []),
                  ]}
                />
                {/* <Select
                  value={assigneeFilter}
                  onChange={setAssigneeFilter}
                  className="w-40"
                  placeholder="All Assignees"
                  showSearch
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  <Select.Option value="all">All Assignees</Select.Option>
                  {employeeOptionsWithNames?.map(
                    (employee: { value: string; label: string }) => (
                      <Select.Option key={employee.value} value={employee.value}>
                        {employee.label}
                      </Select.Option>
                    )
                  )}
                </Select> */}
              </div>
            </div>
            <ConfigDynamicTable
              tableName={TASK_SCHEMA_NAME}
              columns={columns}
              headerTitle=""
              rowKey="id"
              filter={filter}
              toolBarRender={toolBarRender}
              selectedRowKeys={selectedRowKeys}
              setSelectedRowKeys={setSelectedRowKeys}
            />
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default TaskPage;
